import { useForm, FormProvider } from '@xmanscript/useform';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ProductService } from '@/ui/_apiUrls';
import { authenticatedApi } from '@/api/config';
import Button from '../Button';
import FormControl from '../FormControl';
import ErrorLabel from '../FormControl/_lib/ErrorLabel';

interface AddProductDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Record<string, any>;
  onSuccess?: () => void;
  onDelete?: (productToDelete: Record<string, any>) => void;
}

export default function AddProductDrawer({ isOpen, onClose, product, onSuccess, onDelete }: AddProductDrawerProps) {
  const queryClient = useQueryClient();

  // Define mutations before useForm to avoid use-before-define errors
  const { isLoading } = ProductService.post({
    mutationParams: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
        if (onSuccess) onSuccess();
        onClose();
      },
    },
  });

  const { mutate: patchProduct, isLoading: isPatchingProduct } = ProductService.patch({
    mutationParams: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
        if (onSuccess) onSuccess();
        onClose();
      },
    },
  });

  const [mediaUploadError, setMediaUploadError] = useState<string | null>(null);

  const onProductSubmitHandler = async (vals: Record<string, any>) => {
    try {
      const {
        currentPacket: { name, url, thumbnail },
      } = vals;

      if (product) {
        // Update existing product
        const patchData: Record<string, any> = {
          // eslint-disable-next-line no-underscore-dangle
          id: product._id || product.id,
          name: name,
          link: url,
        };

        patchProduct(patchData);

        // If there's a new thumbnail file, upload it using the two-step process
        if (thumbnail && Array.isArray(thumbnail) && thumbnail.length > 0 && thumbnail[0]?.file) {
          const fileToUpload = thumbnail[0].file;
          const formData = new FormData();
          formData.append('file', fileToUpload);

          const productId = product._id || product.id;

          try {
            // First, upload the media file to get mediaId
            const mediaResponse = await authenticatedApi.post('/api/v1/media/', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            // Then, use the mediaId to set the thumbnail for the product
            if (mediaResponse.data && mediaResponse.data.id) {
              await authenticatedApi.post(`/api/v1/product/${productId}/upload-thumbnail/`, {
                mediaId: mediaResponse.data.id,
              });
            }

            queryClient.invalidateQueries({ queryKey: ['products'] });
            if (onSuccess) onSuccess();
            onClose();
          } catch (error) {
            console.error('Error uploading media:', error);
            setMediaUploadError('Failed to upload thumbnail.');
          }
        }
      } else {
        // Create new product first
        let newProductId = null;
        
        const postData: Record<string, any> = {
          name: name,
          link: url,
          featured: true,
        };

        try {
          const productResponse = await authenticatedApi.post('/api/v1/product/', postData);
          newProductId = productResponse.data.result.id;
          
          // If there's a thumbnail file, upload it using the two-step process
          if (thumbnail && Array.isArray(thumbnail) && thumbnail.length > 0 && thumbnail[0]?.file && newProductId) {
            const fileToUpload = thumbnail[0].file;
            const formData = new FormData();
            formData.append('file', fileToUpload);

            try {
              // First, upload the media file to get mediaId
              const mediaResponse = await authenticatedApi.post('/api/v1/media/', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });

              // Then, use the mediaId to set the thumbnail for the product
              if (mediaResponse.data && mediaResponse.data.id) {
                await authenticatedApi.post(`/api/v1/product/${newProductId}/upload-thumbnail/`, {
                  mediaId: mediaResponse.data.id,
                });
              }
            } catch (error) {
              console.error('Error uploading media:', error);
              setMediaUploadError('Failed to upload thumbnail.');
            }
          }

          queryClient.invalidateQueries({ queryKey: ['products'] });
          if (onSuccess) onSuccess();
          onClose();
        } catch (error) {
          console.error('Error creating product:', error);
          queryClient.invalidateQueries({ queryKey: ['products'] });
          if (onSuccess) onSuccess();
          onClose();
        }
      }
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const formMethods = useForm({
    formName: `productUploadForm`,
    initialValues: {
      name: '',
      url: '',
      thumbnail: [],
    },
    submitHandler: onProductSubmitHandler,
    validationSchema: (vals: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { name, url } = vals;
      if (!name) errors.name = 'Product name is required.';
      if (!url) errors.url = 'Product link is required.';

      return errors;
    },
    touchOnChange: true,
    persistValues: false,
  });

  const { register, onSubmitHandler, formState, setValues }: any = formMethods;

  useEffect(() => {
    if (!product) {
      // Reset form to initial values when no product is provided
      setValues({
        name: '',
        url: '',
        thumbnail: [],
      });
      return;
    }

    setValues({
      name: product?.name || '',
      url: product?.link || '',
      // Only set thumbnail if it exists and is a valid URL
      thumbnail:
        product?.thumbnail && (product.thumbnail.startsWith('http://') || product.thumbnail.startsWith('https://'))
          ? [{ url: product?.thumbnail }]
          : [],
    });
  }, [product, setValues]);

  useEffect(() => {
    if (!isOpen) {
      // Clear form manually instead of calling resetForm to avoid console logs
      try {
        setValues({});
      } catch (error) {
        // Silently handle form clearing errors
      }
    }
  }, [isOpen, setValues]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleBackdropClick(e as any);
              }
            }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              duration: 0.3,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close drawer"
                />
              </div>

              {/* Content */}
              <div className="flex flex-col p-4 sm:p-6">
                <FormProvider {...formMethods}>
                  <div className="w-full space-y-6">
                    {/* Header */}
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {product ? 'Edit Highlight' : 'Add New Highlight'}
                      </h2>
                      <p className="mt-2 text-sm text-gray-500">
                        {product ? 'Update your highlight details' : 'Add a new product or highlight to showcase'}
                      </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={onSubmitHandler} className="space-y-6">
                      {/* Name Field */}
                      <div>
                        <FormControl
                          controlType="input"
                          label="Highlight Name"
                          placeholder="Enter Highlight name"
                          {...register('name')}
                        />
                        {(formState as any)?.errors?.name && <ErrorLabel message={(formState as any).errors.name} />}
                      </div>

                      {/* URL Field */}
                      <div>
                        <FormControl
                          controlType="input"
                          label="Highlight's URL"
                          placeholder="https://example.com"
                          {...register('url')}
                        />
                        {(formState as any)?.errors?.url && <ErrorLabel message={(formState as any).errors.url} />}
                      </div>

                      {/* Thumbnail Upload */}
                      <div>
                        <FormControl
                          controlType="upload"
                          label="Highlight's Thumbnail"
                          accept="image/*"
                          {...register('thumbnail')}
                        />
                        {mediaUploadError && <ErrorLabel message={mediaUploadError} />}
                      </div>
                      <div className="flex gap-3 pt-2">
                        {product ? (
                          <>
                            <Button
                              variant="secondary"
                              className="h-12 flex-1 rounded-lg border-2 border-sky-500 bg-white font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                              onClick={() => {
                                if (onDelete && product) onDelete(product);
                              }}
                              type="button"
                            >
                              Delete
                            </Button>
                            <Button
                              variant="primary"
                              className="h-12 flex-1 rounded-lg bg-sky-500 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                              type="submit"
                              disabled={isLoading || isPatchingProduct}
                            >
                              {isLoading || isPatchingProduct ? 'Saving...' : 'Update'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="secondary"
                              className="h-12 flex-1 rounded-lg border-2 border-sky-500 bg-white font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                              onClick={handleClose}
                              type="button"
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              className="h-12 flex-1 rounded-lg bg-sky-500 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                              type="submit"
                              disabled={isLoading}
                            >
                              {isLoading ? 'Saving...' : 'Add'}
                            </Button>
                          </>
                        )}
                      </div>
                    </form>
                  </div>
                </FormProvider>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
}